import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * The portal pages' sticky-header recipe (Dashboard, Favorite Links,
 * Calendar, Clients): a zero-height sentinel sits at the top of the page,
 * and once it scrolls out of view the header condenses. The portal's
 * <main> is the scroll container, so the sentinel is observed against it.
 *
 * Render the returned ref on an empty element directly above the header.
 */
export function useCondensedHeader(): {
  sentinelRef: RefObject<HTMLDivElement>;
  stuck: boolean;
} {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { root: el.closest('main') }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { sentinelRef, stuck };
}

/** Gap kept above and below a screen-filling panel (the page's bottom
 *  padding is the same, so a full scroll lands the panel exactly) */
export const PANEL_GAP = 12;
/** Smallest screen-filling panel, so short windows still get a usable box */
export const MIN_PANEL_HEIGHT = 480;

/**
 * Height for a panel that should fill the screen under the condensed sticky
 * header: the scroll container's visible height minus the condensed header
 * (its control row plus padding — measured before the header sticks, so the
 * value is stable) minus the gaps. Scrolling past whatever sits above the
 * panel then leaves exactly the panel on screen, and the page never jitters
 * between the condensed and expanded header.
 */
export function usePanelHeight(
  anchor: RefObject<HTMLElement | null>,
  controls: RefObject<HTMLElement | null>
): number {
  const [height, setHeight] = useState(MIN_PANEL_HEIGHT);
  useEffect(() => {
    const main = anchor.current?.closest('main');
    const row = controls.current;
    if (!main || !row) return;
    const measure = () => {
      // py-3 padding (24px) around the control row; at least the condensed
      // title's line height on wider screens
      const header = Math.max(row.offsetHeight, 28) + 24;
      setHeight(
        Math.max(MIN_PANEL_HEIGHT, main.clientHeight - header - 2 * PANEL_GAP)
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(main);
    observer.observe(row);
    return () => observer.disconnect();
  }, [anchor, controls]);
  return height;
}
